import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, Coffee } from "lucide-react";
import { Link } from "react-router-dom";

export default function ClaimButton({ cafe }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [claim, setClaim] = useState(null);
  const [form, setForm] = useState({ business_email: "", role: "Owner", evidence: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !cafe?.id) return;
    api.get(`/cafes/${cafe.id}/my-claim`).then(({ data }) => setClaim(data?.status ? data : null)).catch(() => {});
  }, [user, cafe?.id]);

  if (!cafe) return null;

  // If café already has an approved owner, show badge
  if (cafe.owner_user_id) {
    return (
      <span
        data-testid="owner-verified-badge"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#C05A46] border border-[#C05A46]/40 bg-[#C05A46]/8 rounded-full px-3 py-1"
      >
        <BadgeCheck size={13} strokeWidth={1.5} />
        Owner-verified
      </span>
    );
  }

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/cafes/${cafe.id}/claim`, form);
      toast("Claim submitted", { description: "We'll review and get back to you." });
      const { data } = await api.get(`/cafes/${cafe.id}/my-claim`);
      setClaim(data?.status ? data : null);
      setOpen(false);
    } catch (e) {
      toast("Couldn't submit claim", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Link to="/login" data-testid="claim-signin-prompt" className="text-xs text-[#786B63] hover:text-[#C05A46]">
        Own this café? Sign in to claim it →
      </Link>
    );
  }

  if (claim?.status === "pending") {
    return (
      <span className="text-xs text-[#786B63] inline-flex items-center gap-1.5" data-testid="claim-pending">
        <Coffee size={12} strokeWidth={1.5} /> Your claim is under review
      </span>
    );
  }
  if (claim?.status === "rejected") {
    return (
      <span className="text-xs text-[#786B63]" data-testid="claim-rejected">
        Claim not approved — contact support if you think this is a mistake
      </span>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="claim-cafe-btn"
          className="text-xs text-[#786B63] hover:text-[#C05A46] inline-flex items-center gap-1.5"
        >
          <BadgeCheck size={12} strokeWidth={1.5} /> Own this café? Claim it
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#F9F6F0] border-border rounded-sm" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Claim {cafe.name}</DialogTitle>
          <p className="text-sm text-[#786B63]">
            Verified owners can respond to reviews and update their listing. We'll review your claim within a few days.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Business email *</Label>
            <Input
              type="email"
              value={form.business_email}
              onChange={(e) => setForm({ ...form, business_email: e.target.value })}
              placeholder="hello@yourcafe.com"
              className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11"
              data-testid="claim-business-email"
            />
          </div>
          <div>
            <Label>Your role *</Label>
            <Input
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11"
              data-testid="claim-role"
            />
          </div>
          <div>
            <Label>How can we verify you? *</Label>
            <Textarea
              rows={4}
              value={form.evidence}
              onChange={(e) => setForm({ ...form, evidence: e.target.value })}
              placeholder="Link to your café website, social profile, or anything we can use to confirm ownership."
              className="mt-1.5 rounded-sm bg-[#F2EBE1]"
              data-testid="claim-evidence"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
            onClick={submit}
            disabled={saving || !form.business_email || form.evidence.length < 10}
            data-testid="claim-submit"
          >
            {saving ? "Submitting…" : "Submit claim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
